/* eslint-disable no-console */
import Cookies from "js-cookie";

import store from "@utils/redux/store";
import { captureException } from "@utils/sentry";
import { trackEvent } from "@utils/api/tracker";
import {
  shouldAdjustELTV,
  adjustELTV,
  extractAdName,
} from "@utils/eltvAdjustment";
import registerHandlers, { firePixelEvent } from "../pixels/handler";
import { generateEventId } from "@utils/pixels/utils";
import { getHealthyMindRevenueMetrics } from "../pixels/metrics";

const PIXEL_RETRY_DELAY_MS = 5000;
const PIXEL_RETRY_MAX_ATTEMPTS = 10;

// NOTE(nick): Due to GRX-327, we now have two FB pixels, one that uses erev9
// and one that uses eltv13 as the value for the purchase event.
export const FB_EREV9_PIXEL = "1204952429523059";
export const FB_ELTV13_PIXEL = "1557275494440599";
const FB_KR_SANDBOX_PIXEL = "2542854726029439";
const FB_HM_PIXEL = "373833553789964";

const FB_KR_PIXELS = [FB_KR_SANDBOX_PIXEL];
const FB_HW_PIXELS = [FB_EREV9_PIXEL, FB_ELTV13_PIXEL];
const FB_HM_PIXELS = [FB_HM_PIXEL];

const KAKAO_PIXEL = "7044192314154893328";

/**
 * A helper class for sending conversion events.
 */
export default class ConversionTracker {
  constructor() {
    ConversionTracker.initFacebookPixels();
    this.trackFacebookConversion(FB_HW_PIXELS, "PageView", null);
    this.trackFacebookConversion(FB_HM_PIXELS, "PageView", null);
    this.trackFacebookConversion(FB_KR_PIXELS, "PageView", null);

    registerHandlers();
    firePixelEvent("init");

    // Parse the full URL and save it as part of the tracker instance for later use.
    const params = new URL(document.location).searchParams;
    const urlParams = {};
    params.forEach((value, key) => {
      urlParams[key] = value;
    });
    this.urlParams = urlParams;
  }

  /**
   * Helper function to retry firing a pixel if the pixel hasn't loaded yet.
   *
   * NOTE(patrick): If a user has an adblocker that prevents a pixel from loading,
   *                the global variable won't ever load and we'll hit the attempt
   *                limit.
   *
   * @param {string} fnName The name of the function bound to the window object to fire.
   * @param {array} args An array of arguments to pass into fn. It will be spread
   *                     into the params.
   * @param {number} attempt The attempt number, used to track number of retries.
   */
  static firePixelWithRetry(fnName, args = [], attempt = 0) {
    if (window[fnName] && typeof window[fnName] === "function") {
      window[fnName](...args);
    } else if (attempt <= PIXEL_RETRY_MAX_ATTEMPTS) {
      setTimeout(() => {
        ConversionTracker.firePixelWithRetry(fnName, args, attempt + 1);
      }, PIXEL_RETRY_DELAY_MS);
      console.info(
        `window.${fnName} doesn't exist, waiting ${Math.floor(
          PIXEL_RETRY_DELAY_MS / 1000
        )}s before retrying.`
      );
    } else {
      console.error(`window.${fnName} doesn't exist. Max retries reached.`);
    }
  }

  /**
   * Initialize Facebook pixel. Make sure to add the LDU flag if CCPA
   * opt out cookie is set.
   * @param {*} fbParams
   */
  static initFacebookPixels(fbParams = null) {
    // Check to add LDU flag for CCPA opt out.
    const ccpaOptOut = Cookies.get("ccpaOptOut");
    if (ccpaOptOut) {
      window.fbq("dataProcessingOptions", ["LDU"], 1, 1000);
    }

    const initPixel = (pixel) => {
      ConversionTracker.firePixelWithRetry("fbq", ["init", pixel, fbParams]);
    };

    initPixel(FB_EREV9_PIXEL);
    initPixel(FB_ELTV13_PIXEL);
    initPixel(FB_KR_SANDBOX_PIXEL);
    initPixel(FB_HM_PIXEL);
  }

  /**
   * Initializes all advanced matching pixels
   */
  initAdvancedMatching(email, name) {
    this.initFBWithAdvancedMatching(email, name);
  }

  /**
   * Enables the advance matching feature for Facebook Pixel.
   */
  // eslint-disable-next-line class-methods-use-this
  initFBWithAdvancedMatching(email, name) {
    try {
      const fbParams = {};
      if (email && email.length > 0 && name && name.length > 0) {
        fbParams.em = email.toLowerCase();

        const nameParts = name.trim().split(" ");
        [fbParams.fn] = nameParts;
        if (nameParts.length > 1) {
          fbParams.ln = nameParts[nameParts.length - 1];
        }
      }
      const { userData } = store.getState();
      if (userData?.user_id) {
        fbParams.external_id = userData.user_id;
      }

      ConversionTracker.initFacebookPixels(fbParams);
    } catch (e) {
      captureException(e);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  trackLandingPageViewKR() {
    ConversionTracker.trackGoogleAdwordsConversion(
      "AW-867388860/NUZMCMqBpLUBELybzZ0D"
    );

    ConversionTracker.trackKakaoConversion(KAKAO_PIXEL);
  }

  /**
   * Tracks the 'Lead' event.
   */
  trackLead(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      if (surveyAnswers && surveyAnswers.weightLossGoal) {
        fbParams.value = surveyAnswers.weightLossGoal;
      }
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "Lead", fbParams);

      firePixelEvent("lead", { email, name, surveyAnswers });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Lead' event for Healthy Mind users.
   */
  trackLeadHM(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      fbParams.product = "hm";

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "Lead", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "Lead", fbParams);

      firePixelEvent("HMLead", { email, name, surveyAnswers });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Lead' event for Healthy Mind users.
   */
  trackLeadKR(email, name, surveyAnswers) {
    try {
      this.initAdvancedMatching(email, name);

      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {};
      fbParams.product = "hm";
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_KR_PIXELS, "Lead", fbParams);

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/Hb_FCK60nbUBELybzZ0D"
      );

      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "lead");

      window.dataLayer.push({
        event: "lead",
        email,
        name,
        program: "healthy-mind",
        ...surveyAnswers,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Initiate Checkout' event.
   */
  trackInitiateCheckout(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {
        value: plan.lv,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
      };
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "InitiateCheckout", fbParams);

      firePixelEvent("initiateCheckout", { plan, surveyAnswers });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Initiate Checkout' event for Healthy Mind users.
   */
  trackInitiateCheckoutHM(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const fbParams = {
        value: plan.lv,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: "hm",
      };

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_HW_PIXELS, "InitiateCheckout", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "InitiateCheckout", fbParams);
      ConversionTracker.firePixelWithRetry("pintrk", ["track", "addtocart"]);

      firePixelEvent("HMInitiateCheckout", { plan, surveyAnswers });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Initiate Checkout' event for Korean users.
   */
  trackInitiateCheckoutKR(plan, surveyAnswers) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const value = ConversionTracker.getELTVByPlanKR(plan);
      const fbParams = {
        value,
        currency: plan.currency ? plan.currency : "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: plan.curriculum,
      };

      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);
      this.trackFacebookConversion(FB_KR_PIXELS, "InitiateCheckout", fbParams);
      ConversionTracker.firePixelWithRetry("pintrk", ["track", "addtocart"]);

      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/GdHgCKbnv3AQvJvNnQM"
      );

      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "initiateCheckout", {
        ...plan,
        value,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Purchase' event.
   */
  trackPurchase(email, plan, surveyAnswers, order) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const { userData, geoLocation } = store.getState();
      const fbParams = {
        value: order.erev_9_months,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        external_id: userData.user_id,
      };

      if (order.subscriptionId) {
        fbParams.order_id = order.subscriptionId;
      }
      const extraParams = { ...params, ...this.urlParams };
      Object.assign(fbParams, extraParams);

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_HW_PIXELS, "StartTrial", fbParams);
      const eltv13Value = shouldAdjustELTV()
        ? adjustELTV(order.eltv_13_months, geoLocation.country_code)
        : order.eltv_13_months;
      if (eltv13Value !== order.eltv_13_months) {
        trackEvent("AdjustedELTV13", {
          originalValue: order.eltv_13_months,
          adjustedValue: eltv13Value,
          adName: extractAdName(),
        });
      }
      this.trackFacebookConversion([FB_EREV9_PIXEL], "Purchase", fbParams);

      const generatedEventID = generateEventId(userData.user_id);
      const fbParamsELTV13 = {
        ...fbParams,
        value: eltv13Value,
      };
      this.trackFacebookConversion(
        [FB_ELTV13_PIXEL],
        "Purchase",
        fbParamsELTV13,
        generatedEventID
      );
      const fbCapiParams = {
        ...fbParamsELTV13,
        custom_properties: extraParams,
        eventId: generatedEventID,
        email,
      };

      firePixelEvent("purchase", {
        email,
        plan,
        surveyAnswers,
        order,
        userData,
        fbCapiParams,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Purchase' event for Healthy Mind users.
   */
  trackPurchaseHM(email, plan, surveyAnswers, order, isSoldOut = false) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const { erev9, eltv13 } = getHealthyMindRevenueMetrics(isSoldOut, order);
      const { deviceInformation, userData } = store.getState();

      const fbParams = {
        value: erev9,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: "hm",
        device_os: deviceInformation?.device_os,
        external_id: userData.user_id,
      };

      if (order.subscriptionId) {
        fbParams.order_id = order.subscriptionId;
      }
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_HW_PIXELS, "StartTrial", fbParams);
      this.trackFacebookConversion(FB_HM_PIXELS, "StartTrial", fbParams);
      this.trackFacebookConversion([FB_EREV9_PIXEL], "Purchase", fbParams);
      this.trackFacebookConversion([FB_ELTV13_PIXEL], "Purchase", {
        ...fbParams,
        value: eltv13,
      });
      this.trackFacebookConversion(FB_HM_PIXELS, "Purchase", fbParams);

      firePixelEvent("HMPurchase", {
        email,
        plan,
        surveyAnswers,
        order,
        userData,
        isSoldOut,
      });
    } catch (e) {
      captureException(e);
    }
  }

  /**
   * Tracks the 'Purchase' event for Healthy Mind users.
   */
  trackPurchaseKR(email, plan, surveyAnswers, order) {
    try {
      const params = ConversionTracker.getCustomParams(surveyAnswers);
      const value = ConversionTracker.getELTVByPlanKR(plan);
      const { deviceInformation, userData } = store.getState();
      const fbParams = {
        value,
        currency: plan.currency || "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        product: plan.curriculum,
        device_os: deviceInformation?.device_os,
        external_id: userData.user_id,
      };
      if (order.subscriptionId) {
        fbParams.order_id = order.subscriptionId;
      }
      Object.assign(fbParams, params);
      Object.assign(fbParams, this.urlParams);

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      this.trackFacebookConversion(FB_KR_PIXELS, "StartTrial", fbParams);
      this.trackFacebookConversion(FB_KR_PIXELS, "Purchase", fbParams);
      ConversionTracker.trackKakaoConversion(KAKAO_PIXEL, "purchase", {
        ...plan,
        value,
      });
      ConversionTracker.trackGoogleAdwordsConversion(
        "AW-867388860/u_2oCLqv-WwQvJvNnQM",
        fbParams.value,
        fbParams.currency
      );
      window.dataLayer.push({
        event: "purchase",
        program: "dietary",
        email,
        plan,
        ...surveyAnswers,
        order,
      });
    } catch (e) {
      captureException(e);
    }
  }

  trackViewContent() {
    this.trackViewContentWithParams({ value: "2.00", currency: "USD" });
  }

  trackViewContentWithParams(params) {
    this.trackFacebookConversion(FB_HW_PIXELS, "ViewContent", params);

    firePixelEvent("viewContent", params);
  }

  trackViewContentWithParamsExsf0(params) {
    const { serverContext, language } = store.getState();
    if (serverContext.is_paid_traffic && language === "en") {
      this.trackViewContentWithParams(params);
    }
  }

  static getCustomParams(surveyAnswers) {
    // NOTE(di): We would like to parse and send all survey answers to Facebook.
    const params = {};

    // NOTE: Facebook has flagged that we shouldn’t be passing the following parameters from the buyflow.
    const notAllowedParamList = [
      "gender",
      "antibiotic",
      "weight",
      "weightKg",
      "pregnant",
      "email",
      "currentHealthRisk",
      "lifeEvent",
      "lifeEventMale",
      "currentHealthRiskMale",
    ];

    if (surveyAnswers) {
      Object.entries(surveyAnswers).forEach(([key, value]) => {
        if (notAllowedParamList.includes(key)) return;
        if (Array.isArray(value)) {
          params[key] = value.join(",");
        } else {
          params[key] = value;
        }
      });
    }

    // NOTE(di): Since Facebook doesn't allow advance calculation on passed values, we pre-calculate some key values here.
    if (params.weight && params.idealWeight) {
      params.weightLossGoal = params.weight - params.idealWeight;
    } else if (params.weightKg && params.idealWeightKg) {
      const weightLossGoalKg = params.weightKg - params.idealWeightKg;
      params.weightLossGoal = Math.round(weightLossGoalKg * 2.2046);
    } else if (params.weightLossGoalKg) {
      params.weightLossGoal = Math.round(params.weightLossGoalKg * 2.2046);
    }

    return params;
  }

  trackFacebookConversion(pixels, eventName, params, eventId = undefined) {
    pixels.forEach((pixel) => {
      const args = ["trackSingle", pixel, eventName, params];
      if (eventId) {
        args.push({
          eventID: eventId,
        });
      }
      ConversionTracker.firePixelWithRetry("fbq", args);
    });
  }

  // This is a tag manager pixel that's tied to google adwords.
  static trackGoogleAdwordsConversion(sendTo, value = 1.0, currency = "USD") {
    ConversionTracker.firePixelWithRetry("gtag", [
      "event",
      "conversion",
      {
        send_to: sendTo,
        value,
        currency,
      },
    ]);
  }

  static trackKakaoConversion(pid, type, params, attempt = 0) {
    try {
      if (window.kakaoPixel) {
        window.kakaoPixel(pid).pageView(); // type === "visit"
        switch (type) {
          case "initiateCheckout":
            window.kakaoPixel(pid).signUp("SignUp");
            break;
          case "lead":
            window.kakaoPixel(pid).participation("Consulting");
            break;
          case "purchase":
            window.kakaoPixel(pid).purchase({
              total_quantity: "1", // 주문 내 상품 개수(optional)
              total_price: { value: params.value }, // 주문 총 가격(optional)
              currency: params.currency || "KRW", // 주문 가격의 화폐 단위(optional, 기본 값은 KRW)
              products: [
                // 주문 내 상품 정보(optional)
                {
                  name: params.curriculum,
                  content_ids: params.braintree_id,
                  product_type: "product",
                },
              ],
            });
            break;
          default:
            break;
        }

        window.twttr.conversion.trackPid(pid, {
          tw_sale_amount: 0,
          tw_order_quantity: 0,
        });
      } else if (attempt <= PIXEL_RETRY_MAX_ATTEMPTS) {
        setTimeout(() => {
          ConversionTracker.trackKakaoConversion(pid, attempt + 1);
        }, PIXEL_RETRY_DELAY_MS);
      }
    } catch (e) {
      /* NOP */
    }
  }

  static getELTVByPlanKR(plan) {
    const { currency = "KRW", billing_cycle_in_months = 2 } = plan;
    const id = `${currency}_${billing_cycle_in_months}`;
    let eLTV = currency === "KRW" ? 20000 : 20;
    if (id === "KRW_1") {
      eLTV = 10000;
    }
    if (id === "KRW_2") {
      eLTV = 20000;
    }
    if (id === "KRW_4") {
      eLTV = 30000;
    }
    if (id === "KRW_6") {
      eLTV = 35000;
    }
    if (id === "USD_1") {
      eLTV = 10;
    }
    if (id === "USD_2") {
      eLTV = 20;
    }
    if (id === "USD_4") {
      eLTV = 30;
    }
    if (id === "USD_6") {
      eLTV = 35;
    }
    return eLTV;
  }
}
